import React, { useContext, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { usePathname } from 'next/navigation';
import { useToast } from "@/hooks/useToast";
import { filter } from "lodash";

import { FeedbackThankyou } from "./FeedbackThankyou";
import { MultipleFeedback } from "./MultipleFeedback";
import { AddInfoFeedback } from "./AddInfoFeedback";
import { SingleFeedback } from "./SingleFeedback";
import { FeedbackHero } from "./FeedbackHero";
import { Button } from "../Button";

import {
  FEEDBACK_PREVIEW_INDEX,
  handleSaveAndMore,
  handleSubmit,
} from "@/utils/feedbackPreviewNavigation";

import { QRFeedbackContextProps } from "@/contexts/QRFeedbackContext/types";
import { QRFeedbackContext } from "@/contexts/QRFeedbackContext";

export const FeedbackReviewPreview = ({ data, isInitPreview }) => {
  const { themeColor, categories } = data;

  const { t } = useTranslation("feedback");
  const { toast } = useToast();
  const pathname = usePathname()

  const [isRequesting, setIsRequesting] = React.useState(false);

  const qrFeedbackContextData =
    useContext<QRFeedbackContextProps>(QRFeedbackContext);
  const {
    selectedReviewCategory,
    currentView,
    setCurrentView,
    feedbackReviewData
  } = qrFeedbackContextData;

  let hasMultipleCategories = categories?.length > 1;
  
  const customClass = () => {
    if (currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM)
    return "multiple";
    if (currentView === FEEDBACK_PREVIEW_INDEX.THANK_YOU) return "thank-you";
    else return "";
  };

  const isSubmitButtonDisabled = () => {
    if (currentView === FEEDBACK_PREVIEW_INDEX.HOME_MULTIPLE_CATEGORIES) {
      return filter(feedbackReviewData.categories, { rating: null }).length === feedbackReviewData.categories?.length;
    } else if (currentView === FEEDBACK_PREVIEW_INDEX.HOME_SINGLE_CATEGORY) {
      return selectedReviewCategory?.rating === null && filter(feedbackReviewData.categories, { rating: null }).length > 0;
    } else if (currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM) {
      return selectedReviewCategory?.rating === null
    }
  }

  const hasAddInfoForm = () => {
    const { form: { email, name, phone } } = data;
    
    return email || name || phone;
  }

  const onSubmit = async () => {
    try {
      setIsRequesting(true);
      await handleSubmit(qrFeedbackContextData, hasMultipleCategories, pathname, hasAddInfoForm());
      setIsRequesting(false);
    } catch (error) {
      setIsRequesting(false);
      toast({ message: "genericError", type: "error" });
    };
  }

  useEffect(() => {
    setCurrentView(!hasMultipleCategories ? 0 : 1);
  }, [categories]);

  return (
    <>
      <FeedbackHero
        data={data}
        currentView={currentView}
        isInitPreview={isInitPreview}
      />
      <div className={`feedback-content ${customClass()}`}>
        {currentView === FEEDBACK_PREVIEW_INDEX.HOME_SINGLE_CATEGORY && (
          <SingleFeedback
            data={data}
            isInitPreview={isInitPreview}
            hasMultipleCategories={hasMultipleCategories}
            category={selectedReviewCategory}
          />
        )}
        {currentView === FEEDBACK_PREVIEW_INDEX.HOME_MULTIPLE_CATEGORIES && (
          <MultipleFeedback
            data={data}
            currentView={currentView}
            isInitPreview={isInitPreview}
          />
        )}

        {currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM && (
          <SingleFeedback
            data={data}
            isInitPreview={isInitPreview}
            hasMultipleCategories={hasMultipleCategories}
            category={selectedReviewCategory}
          />
        )}

        {currentView === FEEDBACK_PREVIEW_INDEX.ADD_INFO && (
          <AddInfoFeedback
            data={data}
            hasMultipleCategories={hasMultipleCategories}
          />
        )}

        {currentView === FEEDBACK_PREVIEW_INDEX.THANK_YOU && (
          <FeedbackThankyou data={data} />
        )}
      </div>
      {categories?.length > 0 && (
        <div className="feedback-buttons feedback-button">
          {currentView === FEEDBACK_PREVIEW_INDEX.MULTIPLE_CATEGORIES_FORM && (
            <Button
              label={t("_saveMoreButtonLabel")}
              onClick={() => handleSaveAndMore(qrFeedbackContextData)}
              dataQA="feedback-submit-button"
              className="feedback-tertiary-button"
              tertiary
              buttonColor={themeColor.buttonColor}
              small
              disabled={selectedReviewCategory.rating === 0}
            />
          )}
          <Button
            label={
              currentView === FEEDBACK_PREVIEW_INDEX.THANK_YOU
                ? t("_thankYouButtonLabel")
                : t("_sendFeedbackButtonLabel")
            }
            type="button"
            onClick={() => onSubmit()}
            dataQA="feedback-submit-button"
            small
            loading={isRequesting}
            buttonColor={themeColor.buttonColor}
            disabled={isSubmitButtonDisabled()}
          />
        </div>
      )}
    </>
  );
};
